import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Stack, Divider, Paper, Link, IconButton, Popper, ClickAwayListener } from '@mui/material';
import sendRequest from '../utils/sendRequest';
import PoDetailActionButton from '../components/PoDetailActionButton';
import UpdateReceivedButton from '../components/UpdateReceivedButton';
import { STATUSES, RECEIVED_STATUSES } from '../utils/constants';
import FlagIcon from '@mui/icons-material/Flag';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddInvoiceDialog from '../components/AddInvoiceDialog';
import ApproveUpcDialog from '../components/ApproveUpcGenerationDialog';
import WorksheetAutomationButton from '../components/WorksheetAutomationButton';
import { ButtonSpinnerBlue } from '../styled/CustomSpinners';
import DeletePoDialog from '../components/DeletePoDialog';
import UpdateDeliveryDateDialog from '../components/UpdateDeliveryDateDialog';

const PurchaseOrderDetails = ({ addSnackbar, setLoading }) => {
  const { id } = useParams();
  const [poDetails, setPoDetails] = useState(null);
  const [poDocId, setPoDocId] = useState("");
  const [anchorE1, setAnchorE1] = useState(null);

  const moreActionsOpen = Boolean(anchorE1);
  const handleMoreActionsClose = () => setAnchorE1(null);
  const toggleMoreActions = (event) => setAnchorE1(anchorE1 ? null : event.currentTarget);

  const fetchPoDetails = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      if (poDocId === "") {
        const dashData = await sendRequest(`purchase-orders`);
        const map = dashData.po_list.reduce((acc, po) => {
          acc[po.id] = po.doc_id;
          return acc;
        }, {});
        const po_doc_id = map[id];
        setPoDocId(po_doc_id);
        if (!po_doc_id) {
          addSnackbar('Invalid Purchase Order ID.', false);
          return;
        }
        const data = await sendRequest(`purchase-orders/details?po_doc_id=${po_doc_id}`);
        setPoDetails(data);
        return;
      }

      const data = await sendRequest(`purchase-orders/details?po_doc_id=${poDocId}`);
      setPoDetails(data);
    } catch (error) {
      addSnackbar(error.message, false);
    } finally {
      if (!background) setLoading(false);
    }
  }, [id, addSnackbar, setLoading, poDocId]);

  useEffect(() => {
    fetchPoDetails();
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchPoDetails(true);
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [fetchPoDetails]);

  if (!poDetails) return <Typography>Loading...</Typography>;

  const {
    name,
    type,
    currency,
    date_created,
    status,
    currency_conversion,
    spreadsheet_id,
    additional_fees,
    projections,
    po_id,
    units_ordered,
    units_received,
    tracking,
    resolution_comment,
    action,
    comments,
    is_flagged,
    archived,
    invoice_link,
    more_actions,
    upc_generation_allowed,
    upc_generation_requested,
  } = poDetails;

  const totalAdditionalFees = additional_fees
    ? (additional_fees.shipping_fees || 0) +
      (additional_fees.customs_fees || 0) +
      (additional_fees.other_fees || 0)
    : 0;

  const moreActionsDialogs = (action) => {
    if (action === 'add_invoice') {
      return (<AddInvoiceDialog
        po_doc_id={poDocId}
        poName={name}
        addSnackbar={addSnackbar}
        fetchPoDetails={fetchPoDetails}
        closeMoreActions={handleMoreActionsClose}
        key={action}
      />)
    } else if (action === 'add_tracking') {
      return (<UpdateDeliveryDateDialog
        po_doc_id={poDocId}
        poName={name}
        addSnackbar={addSnackbar}
        fetchPoDetails={fetchPoDetails}
        closeMoreActions={handleMoreActionsClose}
        key={action}
      />
      )
    } else if (action === 'worksheet_automation') {
      return (<WorksheetAutomationButton
        po_doc_id={poDocId}
        addSnackbar={addSnackbar}
        closeMoreActions={handleMoreActionsClose}
        key={action}
      />)
    } else if (action === 'approve_upc_generation') {
      return (<ApproveUpcDialog
        po_doc_id={poDocId}
        poName={name}
        addSnackbar={addSnackbar}
        fetchPoDetails={fetchPoDetails}
        closeMoreActions={handleMoreActionsClose}
        key={action}
      />)
    } else if (action === 'delete_approved_po') {
      return (<DeletePoDialog
        isApproved={true}
        buttonLoading={false}
        po_doc_id={poDocId}
        poName={name}
        addSnackbar={addSnackbar}
        fetchPoDetails={fetchPoDetails}
        scPoId={po_id}
        isIcon={false}
        key={action}
      />);
    } else if (action === 'delete_unapproved_po') {
      return (<DeletePoDialog
        isApproved={false}
        buttonLoading={false}
        po_doc_id={poDocId}
        poName={name}
        addSnackbar={addSnackbar}
        fetchPoDetails={fetchPoDetails}
        scPoId={po_id}
        isIcon={false}
        key={action}
      />);
    }
  }

  const capitalizeWords = (str) => str.split("_").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
  const formatDate = (val) => new Date(val).toLocaleDateString("en-US");
  const formatCurrency = (val, showCents = false) => val.toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: showCents ? 2 : 0, maximumFractionDigits: showCents ? 2 : 0
  });
  const formatPercentage = (val) => `${Math.round(val * 100)}%`;
  const formatOriginalCurrency = (val) => val.toLocaleString('en-US', {
    style: 'currency', currency, minimumFractionDigits: 0
  });
  const formatHeader = (header) => header === 'msrp' ? 'MSRP' : header === 'roi' ? 'ROI' : capitalizeWords(header);
  const currencyConversionText = currency !== 'USD' ? ` (1 USD = ${currency_conversion.toFixed(2)} ${currency})` : '';

  const renderStatus = (status) => {
    const isProcessing = status.includes('...');
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component='span'>{status}</Typography>
        {isProcessing && <ButtonSpinnerBlue />}
      </Box>
    )
  }

  const renderTile = (title, children) => (
    <Box sx={{ width: '300px', flexShrink: 0 }}>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', height: '100%', backgroundColor: '#fff', boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.05)' }}>
        <Typography variant="h6">{title}</Typography>
        <Divider sx={{ my: 2 }} />
        {children}
      </Paper>
    </Box>
  );

  const getRelativeTime = (timestamp) => {
    const elapsed = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(elapsed / (1000 * 60));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
    const months = Math.floor(days / 30);
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  };

  const linkify = (text) => {
    const urlRegex = /\b((?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z]{2,})(?:[^\s"<>]*)?)/gi;

    return text.replace(urlRegex, (match) => {
      let href = match;
      if (!/^https?:\/\//i.test(href)) {
        href = 'http://' + href;
      }
      const escapedHref = href.replace(/"/g, '&quot;');
      return `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer">${match}</a>`;
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      <Box sx={{ px: 4, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" gutterBottom>
          {`${name} (${id})${archived ? ' [Archived]' : ''}`}
        </Typography>
        <Stack direction="row" spacing={0}>
          <Box>
            {action && <PoDetailActionButton
              action={action}
              po_doc_id={poDocId}
              poName={name}
              addSnackbar={addSnackbar}
              fetchPoDetails={fetchPoDetails}
              delivDate={tracking ? JSON.parse(tracking.delivery_date) : ""}
              currentPoId={po_id ? po_id : ''}
              poType={type}
              upcGenerationAllowed={upc_generation_allowed}
              upcGenerationRequested={upc_generation_requested}
              shippingFeeDefault={String(additional_fees?.shipping_fees ?? "")}
              customsFeeDefault={String(additional_fees?.customs_fees ?? "")}
              otherFeeDefault={String(additional_fees?.other_fees ?? "")}
            />}
          </Box>
          <Box>
            {more_actions.length && (
              <Box>
                <IconButton onClick={toggleMoreActions}>
                  <MoreVertIcon />
                </IconButton>
                <Popper open={moreActionsOpen} anchorEl={anchorE1} placement='bottom-end' disablePortal>
                  <ClickAwayListener onClickAway={handleMoreActionsClose}>
                    <Paper sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {more_actions.map(action => moreActionsDialogs(action))}
                    </Paper>
                  </ClickAwayListener>
                </Popper>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 4, pb: 4, overflow: 'auto', flexGrow: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>

          {renderTile("General Information", (
            <Stack spacing={2}>
              <Typography><strong>Date Created:</strong> {formatDate(date_created)}</Typography>
              <Typography component='div'><strong>Status:</strong> {renderStatus(status)}</Typography>
              <Typography><strong>Type:</strong> {type}</Typography>
              <Typography><strong>Currency:</strong> {currency}{currencyConversionText}</Typography>
              <Typography><strong>UPC Generation Allowed:</strong> {upc_generation_allowed ? 'Yes' : 'No'}</Typography>
              {(units_ordered > 0) && (<Typography><strong>Units Ordered:</strong> {units_ordered}</Typography>)}
              {RECEIVED_STATUSES.includes(status) && (
                <Typography>
                  <strong>Units Received:</strong> {units_received}
                  {status === STATUSES.pendingReceiving && <UpdateReceivedButton po_doc_id={poDocId} addSnackbar={addSnackbar} fetchPoDetails={fetchPoDetails} />}
                </Typography>
              )}
              {po_id && (
                <Typography>
                  <strong>SellerCloud PO ID: </strong>
                  <Link
                    href={`https://lux.delta.sellercloud.com/purchasing/po-details.aspx?id=${po_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    {po_id}
                  </Link>
                </Typography>
              )}
              {spreadsheet_id && (
                <Typography>
                  <Link
                    href={`https://docs.google.com/spreadsheets/d/${spreadsheet_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    Open Spreadsheet
                  </Link>
                </Typography>
              )}
              {invoice_link && (
                <Typography>
                  <Link
                    href={invoice_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    Invoice
                  </Link>
                </Typography>
              )}
            </Stack>
          ))}

          {additional_fees && renderTile("Additional Fees", (
            <Stack spacing={2}>
              <Typography><strong>Shipping Fees:</strong> {formatCurrency(additional_fees.shipping_fees || 0)}</Typography>
              <Typography><strong>Customs Fees:</strong> {formatCurrency(additional_fees.customs_fees || 0)}</Typography>
              <Typography><strong>Other Fees:</strong> {formatCurrency(additional_fees.other_fees || 0)}</Typography>
              <Typography><strong>Total Additional Fees:</strong> {formatCurrency(totalAdditionalFees)}</Typography>
              <Typography variant="body2" color="textSecondary">(Sum of Shipping, Customs, and Other Fees)</Typography>
            </Stack>
          ))}

          {projections && renderTile("Projections", (
            <Stack spacing={2}>
              {Object.entries(projections).map(([key, value]) => key === 'cost' ? (
                <React.Fragment key="cost">
                  {currency !== 'USD' && <Typography><strong>Cost ({currency}):</strong> {formatOriginalCurrency(value * currency_conversion)}</Typography>}
                  <Typography><strong>Cost (USD):</strong> {formatCurrency(value)}</Typography>
                </React.Fragment>
              ) : (
                <Typography
                  key={key}
                >
                  <strong>{formatHeader(key)}: </strong>
                  <span style={(value < 0) ? { color: '#D32F2F', fontWeight: 'bold' } : null}>
                    {['roi', 'gross_margin', 'net_margin'].includes(key) ? formatPercentage(value) : formatCurrency(value)}
                  </span>
                </Typography>
              ))}
            </Stack>
          ))}

          {tracking && renderTile("Tracking Details", (
            <Stack spacing={2}>
              <Typography><strong>Tracking Number:</strong> {tracking.tracking_number}</Typography>
              <Typography><strong>Carrier:</strong> {tracking.carrier}</Typography>
              <Typography><strong>Expected Delivery Date:</strong> {formatDate(tracking.delivery_date)}</Typography>
              <Typography><strong>Actual Arrival Date:</strong> {tracking.arrival_date === "Pending" ? "Pending" : formatDate(tracking.arrival_date)}</Typography>
            </Stack>
          ))}

          {resolution_comment && renderTile("Resolution", (
            <Stack spacing={2}><Typography sx={{ wordBreak: 'break-word' }}><strong>Comment:</strong> {resolution_comment}</Typography></Stack>
          ))}

          <Link
            href={`/purchase-orders/${id}/comments`}
            underline="none"
            sx={{ width: comments.length ? '616px' : '300px', flexShrink: 0, textDecoration: 'none' }}
          >
            <Paper
              sx={{
                p: 3,
                height: '100%',
                maxHeight: 600,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                backgroundColor: '#fff',
                boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '6px 6px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Latest Comments</Typography>
                {is_flagged && <FlagIcon fontSize="small" sx={{ color: 'error.main' }} />}
              </Box>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                {comments.length > 0 ? (
                  <Stack spacing={3}>
                    {Object.entries(
                      comments
                        .slice(-5)
                        .reduce((grouped, comment) => {
                          const dateStr = new Date(comment.time).toLocaleDateString('en-US');
                          if (!grouped[dateStr]) grouped[dateStr] = [];
                          grouped[dateStr].push(comment);
                          return grouped;
                        }, {})
                    ).map(([date, commentList]) => (
                      <Box key={date}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{date}</Typography>
                        <Stack spacing={1} sx={{ pl: 2 }}>
                          {commentList.map((c, idx) => (
                            <Box key={idx}>
                              <Typography variant="body2" sx={{ display: 'flex', gap: 1 }}>
                                <strong>{c.user}</strong> • {getRelativeTime(c.time)}
                                {c.flagged && <FlagIcon fontSize="small" sx={{ color: c.resolved ? 'success.main' : 'error.main' }} />}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                dangerouslySetInnerHTML={{ __html: linkify(c.comment) }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    No comments yet.
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  bottom: 0,
                  mt: 2,
                  pt: 2,
                  backgroundColor: '#fff',
                  borderTop: '1px solid #eee',
                }}
              >
                <Typography color="primary" fontWeight="medium">View All →</Typography>
              </Box>
            </Paper>
          </Link>

          {poDetails.timeline && renderTile("Timeline", (
            <Stack spacing={3}>
              {Object.entries(
                Object.entries(poDetails.timeline)
                  .filter(([, val]) => val !== null)
                  .map(([event, { date, user }]) => ({ event, user, date }))
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .reduce((grouped, { event, user, date }) => {
                    const dateStr = new Date(date).toLocaleDateString('en-US');
                    if (!grouped[dateStr]) grouped[dateStr] = [];
                    grouped[dateStr].push({ event, user });
                    return grouped;
                  }, {})
              ).map(([date, entries], idx) => (
                <React.Fragment key={date}>
                  {idx !== 0 && <Divider sx={{ my: 2 }} />}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{date}</Typography>
                    <Stack spacing={1} sx={{ pl: 2 }}>
                      {entries.map(({ event, user }, entryIdx) => (
                        <Typography key={entryIdx} variant="body2"><strong>{capitalizeWords(event)}:</strong> {user}</Typography>
                      ))}
                    </Stack>
                  </Box>
                </React.Fragment>
              ))}
            </Stack>
          ))}

        </Box>
      </Box>
    </Box>
  );
};

export default PurchaseOrderDetails;
